/*/=======================================================================================*\
|| IMPORTS DE DEPENDENCIAS                                                                 ||
\*\=======================================================================================/*/
import { useState } from 'react';
import { ROUTES } from '../../const/routes';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useTranslation } from 'react-i18next';

/*/=======================================================================================\*\
|| IMPORTS DE COMPONENTES PROPIOS DEL PROYECTO                                             ||
\*\=======================================================================================/*/
import styles from './Register.module.css';
import BannerLogo from '../../components/BannerLogo/BannerLogo';
import ImagenTragamonedasInicio from '../../components/ImagenTragamonedasInicio/ImagenTragamonedasInicio';
import BotonGrandeDinamico from '../../components/Botones/BotonGrandeDinamico/BotonGrandeDinamico';
import Footer from '../../components/Footer/Footer';

const Register = () => {
  const { t } = useTranslation();
  const [formData, setFormData] = useState({
    email: '',
    nick_usuario: '',
    nombre_usuario: '',
    apellido_usuario: '',
    fecha_nacimiento: '',
    contrasenia: '',
  });

  const [fieldErrors, setFieldErrors] = useState({
    email: '',
    nick_usuario: '',
    nombre_usuario: '',
    apellido_usuario: '',
    fecha_nacimiento: '',
    contrasenia: '',
  });

  const [success, setSuccess] = useState(false); // Estado para mensaje de éxito
  const [error, setError] = useState(null); // Estado para mensaje de error
  const [isSubmitting, setIsSubmitting] = useState(false); // Evita envíos duplicados
  const navigate = useNavigate();

  // Maneja cambios en los campos del formulario
  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({
      ...formData,
      [name]: value,
    });
    // Limpiar error del campo al escribir
    if (fieldErrors[name]) {
      setFieldErrors({
        ...fieldErrors,
        [name]: '',
      });
    }
  };

  // Funciones de validación
  const validateEmail = (email) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!email) return t('register.emailRequired');
    if (!emailRegex.test(email)) return t('register.emailFormatInvalid');
    return '';
  };

  const validateNick = (nick) => {
    if (!nick) return t('register.nickRequired');
    if (nick.length < 3) return t('register.nickMinLength');
    return '';
  };

  const validateNombre = (nombre) => {
    if (!nombre) return t('register.nameRequired');
    if (nombre.length < 2) return t('register.nameMinLength');
    return '';
  };

  const validateApellido = (apellido) => {
    if (!apellido) return t('register.lastNameRequired');
    if (apellido.length < 2) return t('register.lastNameMinLength');
    return '';
  };

  const validateFechaNacimiento = (fecha) => {
    if (!fecha) return t('register.birthdateRequired');
    const hoy = new Date();
    const fechaNac = new Date(fecha);
    const edad = hoy.getFullYear() - fechaNac.getFullYear();
    if (edad < 18) return t('register.ageRestriction');
    return '';
  };

  const validatePassword = (password) => {
    if (!password) return t('register.passwordRequired');
    if (password.length < 6) return t('register.passwordMinLength');
    return '';
  };

  // Maneja el envío del formulario
  const handleRegister = async (e) => {
    e.preventDefault();
    if (isSubmitting) return; // Ya hay un registro en curso
    setError(null); // Reiniciamos el estado de error
    setSuccess(false); // Reiniciamos el estado de éxito

    // Validar todos los campos
    const errors = {
      email: validateEmail(formData.email),
      nick_usuario: validateNick(formData.nick_usuario),
      nombre_usuario: validateNombre(formData.nombre_usuario),
      apellido_usuario: validateApellido(formData.apellido_usuario),
      fecha_nacimiento: validateFechaNacimiento(formData.fecha_nacimiento),
      contrasenia: validatePassword(formData.contrasenia),
    };

    setFieldErrors(errors);

    // Si hay algún error, no continuar
    if (Object.values(errors).some((error) => error !== '')) {
      return;
    }

    setIsSubmitting(true);

    try {
      await axios.post(
        `${process.env.REACT_APP_DIRECCION}/api/usuario/createUsuario`,
        formData,
        {
          withCredentials: true,
        }
      );

      // Se mantiene isSubmitting en true hasta redirigir, para no permitir
      // un segundo registro durante el mensaje de éxito
      setSuccess(true); // Mensaje de éxito
      setTimeout(() => {
        navigate(ROUTES.verificarEmail, { state: { email: formData.email } });
      }, 3000);
    } catch (err) {
      setIsSubmitting(false);
      if (err.response && err.response.data.errors) {
        // Captura los errores de validación y los almacena en el estado
        const errorMessages = err.response.data.errors
          .map((error) => error.msg)
          .join(' | ');
        setError(errorMessages); // Mostrar los errores de validación
      } else if (err.response && err.response.data.msg) {
        setError(err.response.data.msg); // Mensaje de error desde el backend
      } else {
        setError(t('register.errorServer'));
      }
    }
  };

  return (
    <div className={'flex flex-col min-h-screen ' + styles.registerContainer}>
      <div className="flex flex-col flex-grow items-center justify-between text-center px-4">
        <BannerLogo />

        <div className="flex flex-col lg:flex-row justify-center items-center lg:items-end w-full max-w-5xl gap-4 lg:gap-8 mb-4">
          <div className="w-full lg:w-auto flex justify-center order-1 lg:order-1">
            <ImagenTragamonedasInicio />
          </div>

          <div
            className={`p-4 lg:p-5 w-full max-w-md lg:max-w-xl space-y-2 flex flex-col bg-bgContenedorOscuro rounded-lg
                      border-solid border-4 border-borderContenedorOscuro shadow-shadowContenedorOscuro
                      shadow-[0_0_30px_rgba(100,116,139,0.9)] transition-all duration-300 ease-in-out hover:shadow-[0_0_40px_rgba(100,116,139,1)] order-2 lg:order-2`}
          >
            <h2 className="text-lg lg:text-xl text-yellow-500 font-bold mb-1.5">
              {t('register.title')}
            </h2>
            <p className="text-xs text-gray-300 mb-2">
              {t('register.subtitle1')} <br />
              {t('register.subtitle2')}
            </p>
            <form onSubmit={handleRegister}>
              {/* Email */}
              <div className="mb-2">
                <label className="block text-left text-gray-300 text-xs font-bold mb-1">
                  {t('register.emailLabel')}
                </label>
                <input
                  type="email"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  className={`w-full px-3 py-1.5 text-sm text-gray-900 bg-gray-200 rounded-lg border-2 focus:outline-none ${
                    fieldErrors.email
                      ? 'border-red-500 focus:border-red-600'
                      : 'border-gray-300 focus:border-indigo-600'
                  }`}
                  placeholder={t('register.emailPlaceholder')}
                />
                {fieldErrors.email && (
                  <p className="text-red-400 text-xs mt-2">
                    {fieldErrors.email}
                  </p>
                )}
              </div>

              {/* Nick de Usuario */}
              <div className="mb-2">
                <label className="block text-left text-gray-300 text-xs font-bold mb-1">
                  {t('register.nickLabel')}
                </label>
                <input
                  type="text"
                  name="nick_usuario"
                  value={formData.nick_usuario}
                  onChange={handleChange}
                  className={`w-full px-3 py-1.5 text-sm text-gray-900 bg-gray-200 rounded-lg border-2 focus:outline-none ${
                    fieldErrors.nick_usuario
                      ? 'border-red-500 focus:border-red-600'
                      : 'border-gray-300 focus:border-indigo-600'
                  }`}
                  placeholder={t('register.nickPlaceholder')}
                  maxLength={15}
                />
                {fieldErrors.nick_usuario && (
                  <p className="text-red-400 text-xs mt-2">
                    {fieldErrors.nick_usuario}
                  </p>
                )}
              </div>

              {/* Nombre */}
              <div className="mb-2">
                <label className="block text-left text-gray-300 text-xs font-bold mb-1">
                  {t('register.nameLabel')}
                </label>
                <input
                  type="text"
                  name="nombre_usuario"
                  value={formData.nombre_usuario}
                  onChange={handleChange}
                  className={`w-full px-3 py-1.5 text-sm text-gray-900 bg-gray-200 rounded-lg border-2 focus:outline-none ${
                    fieldErrors.nombre_usuario
                      ? 'border-red-500 focus:border-red-600'
                      : 'border-gray-300 focus:border-indigo-600'
                  }`}
                  placeholder={t('register.namePlaceholder')}
                  maxLength={20}
                />
                {fieldErrors.nombre_usuario && (
                  <p className="text-red-400 text-xs mt-2">
                    {fieldErrors.nombre_usuario}
                  </p>
                )}
              </div>

              {/* Apellido */}
              <div className="mb-2">
                <label className="block text-left text-gray-300 text-xs font-bold mb-1">
                  {t('register.lastNameLabel')}
                </label>
                <input
                  type="text"
                  name="apellido_usuario"
                  value={formData.apellido_usuario}
                  onChange={handleChange}
                  className={`w-full px-3 py-1.5 text-sm text-gray-900 bg-gray-200 rounded-lg border-2 focus:outline-none ${
                    fieldErrors.apellido_usuario
                      ? 'border-red-500 focus:border-red-600'
                      : 'border-gray-300 focus:border-indigo-600'
                  }`}
                  placeholder={t('register.lastNamePlaceholder')}
                  maxLength={20}
                />
                {fieldErrors.apellido_usuario && (
                  <p className="text-red-400 text-xs mt-2">
                    {fieldErrors.apellido_usuario}
                  </p>
                )}
              </div>

              {/* Fecha de Nacimiento */}
              <div className="mb-2">
                <label className="block text-left text-gray-300 text-xs font-bold mb-1">
                  {t('register.birthdateLabel')}
                </label>
                <input
                  type="date"
                  name="fecha_nacimiento"
                  value={formData.fecha_nacimiento}
                  onChange={handleChange}
                  className={`w-full px-3 py-1.5 text-sm text-gray-900 bg-gray-200 rounded-lg border-2 focus:outline-none ${
                    fieldErrors.fecha_nacimiento
                      ? 'border-red-500 focus:border-red-600'
                      : 'border-gray-300 focus:border-indigo-600'
                  }`}
                />
                {fieldErrors.fecha_nacimiento && (
                  <p className="text-red-400 text-xs mt-2">
                    {fieldErrors.fecha_nacimiento}
                  </p>
                )}
              </div>

              {/* Contraseña */}
              <div className="mb-2">
                <label className="block text-left text-gray-300 text-xs font-bold mb-1">
                  {t('register.passwordLabel')}
                </label>
                <input
                  type="password"
                  name="contrasenia"
                  value={formData.contrasenia}
                  onChange={handleChange}
                  className={`w-full px-3 py-1.5 text-sm text-gray-900 bg-gray-200 rounded-lg border-2 focus:outline-none ${
                    fieldErrors.contrasenia
                      ? 'border-red-500 focus:border-red-600'
                      : 'border-gray-300 focus:border-indigo-600'
                  }`}
                  placeholder={t('register.passwordPlaceholder')}
                />
                {fieldErrors.contrasenia && (
                  <p className="text-red-400 text-xs mt-2">
                    {fieldErrors.contrasenia}
                  </p>
                )}
              </div>

              <BotonGrandeDinamico
                texto={
                  isSubmitting ? t('register.submitting') : t('register.title')
                }
                bgColor="green"
                tipoBoton="submit"
              />
            </form>

            <div className="mt-4 h-9">
              {success && (
                <p className="text-green-400 text-xs mt-1">
                  {t('register.successMessage')}
                </p>
              )}
              {error && <p className="text-red-400 text-xs mt-1">{error}</p>}
            </div>
            <p className="text-gray-400 text-xs">
              {t('register.hasAccount')}{' '}
              <a href={ROUTES.login} className="text-blue-400 hover:underline">
                {t('register.loginLink')}
              </a>
            </p>
            <button
              onClick={() => navigate(ROUTES.inicio)}
              className="text-blue-400 hover:underline text-sm"
            >
              {t('login.backToHome')}
            </button>
          </div>
        </div>
      </div>

      <div className="w-full">
        <Footer />
      </div>
    </div>
  );
};
export default Register;
